import cloneDeep from "lodash/cloneDeep";
import { useMemo } from "react";
import { useDispatch } from "react-redux";

import {
  dataSourceIdToName,
  dataSourceTypesToID,
  MSP_ACCOUNT_STATE,
  PRODUCT_ID,
  toBrandTypeFormValue,
} from "consts";
import { useMSP, useStorageRegions, useMspList } from "queryHooks";
import useEditionList from "queryHooks/useEditionList/useEditionList";
import useProductDatasource from "queryHooks/useMspProducts/useProductDatasource";
import useSkuList from "queryHooks/useSkuList/useSkuList";
import useUpdateMSP from "queryHooks/useUpdateMSP/useUpdateMSP";
import { closeModal } from "slices/modalsSlice";
import { asLocalTime } from "utils/timezone";
import {
  isDruInvestigateEnabled,
  isEducationLicenseEnabled,
} from "../Summary";

const productsInitialValues = [
  {
    enabled: false,
    productID: PRODUCT_ID.enterpriseWorkloads,
    skus: [],
    awsStorageRegions: [],
    azureStorageRegions: [],
    datasources: [],
  },
  {
    enabled: false,
    productID: PRODUCT_ID.saasAppsAndEndpoints,
    skus: [],
    awsStorageRegions: [],
    azureStorageRegions: [],
    datasources: [],
    features: [],
    educationLicense: false,
  },
];

const useUpdateMSPForm = (mspId) => {
  const dispatch = useDispatch();

  const queryConfig = useMemo(
    () => ({
      onError: () => {
        dispatch(closeModal("UPDATE_MSP_FORM"));
      },
    }),
    [dispatch]
  );

  const { data: mspData } = useMSP(mspId, queryConfig);
  const { data: storageRegionListData } = useStorageRegions(queryConfig);
  const { data: skuListData } = useSkuList(queryConfig);
  const { data: editionListData } = useEditionList(queryConfig);
  const { data: saasAppsAndEndpointsDatasource } = useProductDatasource({
    productId: PRODUCT_ID.saasAppsAndEndpoints,
  });
  const { mspList: mspListForDropdown, canFetchMore: isLoadingMspList } =
    useMspList({ includeDetails: false, pageSize: 1000 });

  const useMutation = useUpdateMSP(mspId);

  const isParentMsp = useMemo(() => {
    if (!mspListForDropdown || !mspId) return false;
    return mspListForDropdown.some((msp) => msp.parentId === mspId);
  }, [mspListForDropdown, mspId]);

  // Filter MSPs: exclude current MSP and any child MSPs (those with parentId)
  const parentMspOptions = useMemo(() => {
    if (!mspListForDropdown || !mspData) return [];
    return mspListForDropdown
      .filter(
        (msp) =>
          msp.id !== mspId && // Exclude current MSP
          !msp.parentId && // Exclude MSPs that are already children
          msp.status === MSP_ACCOUNT_STATE.ready && // Only ready MSPs
          msp.edition !== "evaluation"
      )
      .map((msp) => ({
        value: msp.id,
        label: msp.organizationName,
        expiryDate: msp.expiryDate,
        sfdcId: msp.sfdcId,
      }));
  }, [mspListForDropdown, mspData, mspId]);

  const initialValues = useMemo(() => {
    if (
      mspData &&
      storageRegionListData &&
      skuListData &&
      editionListData &&
      saasAppsAndEndpointsDatasource &&
      mspListForDropdown
    ) {
      const enterpriseWorkloadDetails = cloneDeep(
        mspData.products.find(
          (details) => details.productID === PRODUCT_ID.enterpriseWorkloads
        )
      );

      if (enterpriseWorkloadDetails) {
        enterpriseWorkloadDetails.enabled = true;
        enterpriseWorkloadDetails.productID = PRODUCT_ID.enterpriseWorkloads;
        enterpriseWorkloadDetails.skus = skuListData.list
          .filter(({ id }) => {
            return enterpriseWorkloadDetails.skus.some((sku) => sku === id);
          })
          .map(({ name, id }) => ({
            label: name,
            value: id,
          }));

        enterpriseWorkloadDetails.awsStorageRegions =
          enterpriseWorkloadDetails.awsStorageRegions.map((regionName) => ({
            label: regionName,
            value: regionName,
            isFixed: true,
          }));

        enterpriseWorkloadDetails.azureStorageRegions =
          enterpriseWorkloadDetails.azureStorageRegions.map((regionName) => ({
            label: regionName,
            value: regionName,
            isFixed: true,
          }));

        enterpriseWorkloadDetails.datasources = [
          {
            name: "Enterprise Workloads",
            isEnabled: true,
          },
        ];
      }

      const saasAppsAndEndpointsDetails = cloneDeep(
        mspData.products.find(
          (details) => details.productID === PRODUCT_ID.saasAppsAndEndpoints
        )
      );

      if (saasAppsAndEndpointsDetails) {
        saasAppsAndEndpointsDetails.enabled = true;
        saasAppsAndEndpointsDetails.educationLicense =
          isEducationLicenseEnabled(saasAppsAndEndpointsDetails.features);
        saasAppsAndEndpointsDetails.skus = skuListData.list
          .filter(({ id }) => {
            return saasAppsAndEndpointsDetails.skus.some((sku) => sku === id);
          })
          .map(({ name, id }) => ({ label: name, value: id }));

        saasAppsAndEndpointsDetails.awsStorageRegions =
          saasAppsAndEndpointsDetails.awsStorageRegions.map((regionName) => ({
            label: regionName,
            value: regionName,
            isFixed: true,
          }));

        saasAppsAndEndpointsDetails.azureStorageRegions = (
          saasAppsAndEndpointsDetails.azureStorageRegions || []
        ).map((regionName) => ({
          label: regionName,
          value: regionName,
          isFixed: true,
        }));

        saasAppsAndEndpointsDetails.datasources = saasAppsAndEndpointsDatasource
          .filter((datasource) =>
            saasAppsAndEndpointsDetails?.datasources?.some(
              (ds) =>
                dataSourceTypesToID[ds.name] === datasource.datasourceID &&
                ds.isEnabled
            )
          )
          .map((datasource) => ({
            value: datasource.datasourceID,
            label: dataSourceIdToName[datasource.datasourceID],
          }));
      }
      const edition = editionListData.list.find(
        ({ id }) => id === mspData.edition
      );

      // Find parent MSP if parentId exists
      const parentMsp =
        mspData.parentId && mspListForDropdown
          ? mspListForDropdown.find((msp) => msp.id === mspData.parentId)
          : null;

      const parentMspOption = parentMsp
        ? {
            value: parentMsp.id,
            label: parentMsp.organizationName,
            expiryDate: parentMsp.expiryDate,
            sfdcId: parentMsp.sfdcId,
          }
        : null;

      return {
        organizationName: mspData.organizationName,
        email: mspData.email,
        sfdcId: mspData.sfdcId,
        brandType: toBrandTypeFormValue(mspData.brandType),
        edition: {
          label: edition.name,
          value: edition.id,
          isDisabled: false,
          isFixed: false,
        },
        expiryDate: asLocalTime(mspData.expiryDate),
        parentMsp: parentMspOption,
        druInvestigate: isDruInvestigateEnabled(mspData.features),
        products: [
          enterpriseWorkloadDetails || productsInitialValues[0],
          saasAppsAndEndpointsDetails || productsInitialValues[1],
        ],
      };
    }
  }, [
    editionListData,
    mspData,
    skuListData,
    storageRegionListData,
    saasAppsAndEndpointsDatasource,
    mspListForDropdown,
  ]);

  return [
    {
      initialValues,
      isLoadingMspList,
      parentMspOptions,
      isParentMsp,
    },
    useMutation,
  ];
};

export default useUpdateMSPForm;
